<template>
    <v-form @submit.prevent="addUpdate" ref="form">
        <v-card class="rounded-4">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold">{{ mode }} Salary & Pay Grade</span>
                <v-spacer></v-spacer>
                <help-link appId="compensation" pageId="add-salary" color="black"></help-link>
                <v-btn icon @click.native="closeDialog()" >
                    <v-icon >close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text>
                <v-layout>
                    <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                        {{errorMessage}}
                    </v-alert>  
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 sm4 class="content-row">
                        <v-select
                            label="Year"
                            :items="isEditMode?fiscal_years_all:fiscal_years"
                            :rules="[rules.required]" 
                            item-text="fiscalYear"
                            item-value="fiscalPeriodId"
                            :disabled="disabled||isEditMode"
                            v-model="fiscalPeriodId"
                            @change="effectiveDate=null"
                        ></v-select>
                    </v-flex>
                    <v-flex v-if="isMonthlySalaryType" xs12 sm4 class="content-row">                
                        <v-text-field label="Monthly Basic Salary" 
                            v-model="monthlyBasicSalary" 
                            :disabled="disabled"
                            :rules="[rules.required_number,rules.max_salary]"
                            type="number"
                            ></v-text-field>  
                    </v-flex>
                    <v-flex v-else xs12 sm4 class="content-row">                
                        <v-text-field label="Hourly Rate" 
                            v-model="hourlySalary" 
                            :disabled="disabled"
                            :rules="[rules.required_number,rules.max_hourly_rate]"
                            type="number"
                            ></v-text-field>  
                    </v-flex>
                    <v-flex xs12 sm4 class="content-row">
                        <v-menu v-model="menuEffectiveDate" :close-on-content-click="false" :nudge-right="40"
                            lazy transition="scale-transition" offset-y full-width min-width="290px">
                            <template v-slot:activator="{ on }">
                                <v-text-field v-model="effectiveDate" label="Effective Date" readonly v-on="on" :rules="[rules.required]" :disabled="disabled||!fiscalPeriodId"></v-text-field>
                            </template>
                            <v-date-picker no-title v-model="effectiveDate" :allowed-dates="allowedDates" @input="menuEffectiveDate=false"></v-date-picker>
                        </v-menu>
                    </v-flex>
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 sm12 class="content-row">         
                        <v-select
                            label="Pay Grade"
                            :items="pay_grades"
                            :rules="[rules.required]" 
                            item-text="description"
                            item-value="payGradeId"
                            :disabled="disabled"
                            v-model="payGradeId"             
                        ></v-select>
                    </v-flex>
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 class="content-row">         
                        <v-switch
                            v-model="otPayEligible"
                            label="Overtime Pay Eligible"
                            color="primary"
                            hide-details
                            style="margin:0px 0px 10px 0px;"
                        ></v-switch>
                    </v-flex>
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 class="content-row">         
                        <v-switch
                            v-model="ndPayEligible"
                            label="Night Differential Pay Eligible"
                            color="primary"
                            hide-details
                            style="margin:0px 0px 10px 0px;"
                        ></v-switch>
                    </v-flex>
                </v-layout>
                <v-layout row wrap v-if="showComment"> 
                    <v-flex xs12 class="content-row-comment">         
                        <v-flex xs12>
                            <v-textarea v-model="remarks" label="Comments" rows="2" color="primary"></v-textarea>
                        </v-flex>      
                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="mr-20">
                <v-spacer></v-spacer>
                <v-btn icon @click="showComment=!showComment" title="Add Comment">
                    <v-icon>comment</v-icon>
                </v-btn>
                <v-btn color="primary" type="submit" :disabled="disabled" :loading="loading" dark>Save</v-btn>
                <v-btn v-if="mode!='Update'" @click="clearForm" :disabled="disabled" >Clear</v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>

import payGradeApi from '@/api/paygrade'
import compAllocApi from '@/api/compallocation'
import numFormat from '@/common/num_format'
import salaryTypes from '@/constants/salary-types'

export default {
    data () {
        return {
            email: null,
            loading: false,
            disabled: false,
            error: false,
            errorMessage: null,
            menuEffectiveDate: false,
            rules: {
                required: value => !!value || 'Required field',
                required_number: value => !!value || 'Required field. Number only.',
                max_salary: value => value<=1000000 || 'Invalid Amount. Maximum of 1,000,000.00',
                max_hourly_rate: value => value<=5000 || 'Invalid Hourly Rate',
            },
            mode: null,
            showComment: false,
            payGradeId: null,
            pay_grades: [],
            compensationId: '0',
            basicSalary: null,
            selectedUserId: null,
            remarks: "",
            monthlyBasicSalary: null,   //this is used if SalaryType is 1 (monthly rate)
            hourlySalary: null,         //this is used if SalaryType is 2 (hourly rate)
            salaryType: null,
            fiscalPeriodId: null,
            otPayEligible: false,
            ndPayEligible: false,
            effectiveDate: null,
        }
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
    },
    methods: {
        allowedDates(val) {
            if (!val || !this.selectedYear) return false

            const date = this.$moment(val)
            const currentYear = this.$moment().year()
            const selectedYear = this.selectedYear

            let minDate
            let maxDate

            if (selectedYear > currentYear) {
                // Future year: Jan 1 – Dec 31
                minDate = this.$moment(`${selectedYear}-01-01`)
                maxDate = this.$moment(`${selectedYear}-12-31`)
            } else {
                // Current year: previous month → Dec 31 of current year
                minDate = this.$moment()
                    .subtract(1, 'month')
                    .startOf('month')

                maxDate = this.$moment(`${currentYear}-12-31`)
            }

            return date.isSameOrAfter(minDate, 'day') &&
                date.isSameOrBefore(maxDate, 'day')
        },
        formatNumber() {
            this.salaryStart = this.salaryStart.toLocaleString()
        },
        async setAdd(data) {
            this.clearForm()
            
            if (!this.pay_grades.length) await this.getpay_grades()
            
            this.mode = data.mode
            this.compensationId = data.compensationId
            this.selectedUserId = data.userId
            this.salaryType = data.salaryType
        },
        async setEdit(data) {
            if (!this.pay_grades.length) await this.getpay_grades()
            
            this.mode = data.mode
            this.selectedUserId = data.userId
            this.salaryType = data.salaryType
            this.compensationId = data.compensationId
            this.fiscalPeriodId = data.fiscalPeriodId

            const effectiveDate = data.effectiveDate && 
                                this.$moment(data.effectiveDate).format('YYYY-MM-DD') > '0001-01-01' ? 
                                data.effectiveDate : new Date()

            
            this.payGradeId = data.payGradeId == 0 ? null : data.payGradeId
            this.monthlyBasicSalary = data.monthlyBasicSalary == 0 ? null : data.monthlyBasicSalary
            this.hourlySalary = data.hourlySalary
            this.otPayEligible = data.otPayEligible
            this.ndPayEligible = data.ndPayEligible
            this.effectiveDate = this.$moment(effectiveDate).format('YYYY-MM-DD')
            this.remarks = "" 
        },
        async addUpdate() {
            if (this.$refs.form.validate()) {
                try {
                    this.loading = true
                    this.disabled = true
                    if (this.$refs.form.validate()) {
                        var data = {
                            compensationId: this.compensationId,
                            userId: this.selectedUserId,
                            payGradeId: this.payGradeId,
                            monthlyBasicSalary: this.monthlyBasicSalary || 0,
                            hourlySalary: this.hourlySalary,
                            otPayEligible: this.otPayEligible,
                            ndPayEligible: this.ndPayEligible,
                            effectiveDate: this.effectiveDate,
                            remarks: this.remarks,
                            fiscalPeriodId: this.fiscalPeriodId
                        }
                        const response = await compAllocApi.upsert(data)
                        if (response.status == 200) {
                            this.$emit('update:dialogAddEditSalary', false)
                            this.$emit('send', '')
                        }
                    }
                    this.loading = false
                    this.disabled = false
                }catch (error) {
                    this.error = true
                    this.errorMessage = error
                }
            }
        },
        closeDialog() {
            this.$emit('update:dialogAddEditSalary', false)
        },
        clearForm() {
            this.$refs.form.reset()
            this.loading = false
            this.error = false
            this.errorMessage = ''
        },
        async getpay_grades() {
            try{
                const response = await payGradeApi.getByCompany()
                if (response && response.status == 200) {
                    response.data.items.forEach(item => {
                        this.pay_grades.push(
                            {
                                payGradeId: item.payGradeId,
                                description: `${item.code} - ${item.role} (${numFormat.formatNumber(item.salaryStart)} to ${numFormat.formatNumber(item.salaryEnd)})`
                            }
                        )
                    })
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        }
    },
    created() {},
    computed: {
        isMonthlySalaryType() {
            if (this.salaryType) {
                const salaryType = salaryTypes.find(x => x.salaryType == this.salaryType)
                if (salaryType)
                    return salaryType.name == 'Monthly'
                return true
            }
        },
        fiscal_years() {
            const currentYear = new Date().getFullYear()
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods.filter(x => x.fiscalYear >= currentYear)
            }
            return [{fiscalYear: currentYear}]
        },
        fiscal_years_all() {
            const currentYear = new Date().getFullYear()
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods
            }
            return [{fiscalYear: currentYear}]
        },
        selectedYear() {
            const selected = this.fiscal_years_all.find(x => x.fiscalPeriodId == this.fiscalPeriodId)
            if (selected) return selected.fiscalYear
            return new Date().getFullYear()
        },
        isEditMode() {
            return this.mode == 'Update'
        }
    },
}
</script>
<style scoped>
.content-row {
    padding: 0px 20px 0px 20px;
}
.content-row-comment {
    margin: 10px 25px -25px 25px;
}
</style>